/**
 * The one place the app learns whether it has a server, and the one place it says why not.
 *
 * Paint 6000 is local-first and stays that way: nothing here is imported on the path to first paint,
 * nothing here blocks a sheet from opening, and every failure resolves to a sentence rather than
 * to a spinner. There are exactly four reasons the cloud can be unavailable, they are enumerated
 * below, and each one has an answer a person can act on. "Something went wrong" is not one of
 * them.
 *
 * ── Credentials ────────────────────────────────────────────────────────────────────────────
 *
 * `VITE_SUPABASE_URL` and `VITE_SUPABASE_PUBLISHABLE_KEY` are read from `import.meta.env` and
 * from nowhere else. They are never written to a log, never put in an error message, and never
 * stored anywhere this module does not control — a key in a console line ends up in a screenshot
 * in a bug report. The publishable key is designed to be in a browser bundle; it is safe there
 * precisely because row-level security decides what it can reach, which is why `supabase/*.sql`
 * enables RLS on every table without exception.
 */

import type { SupabaseClient, User } from '@supabase/supabase-js';

const url = (): string => (import.meta.env.VITE_SUPABASE_URL ?? '').trim();
const key = (): string => (import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? '').trim();

/** True when both values are present. Says nothing about whether they work. */
export const supabaseConfigured = (): boolean => url().length > 0 && key().length > 0;

let client: SupabaseClient | null = null;
let clientFailed = false;

/**
 * The shared client, built on first use.
 *
 * Lazy because `@supabase/supabase-js` is a substantial module and a solo sheet must never wait
 * on it. Everything above this returns `null` cleanly when there is nothing to build.
 */
export async function getSupabase(): Promise<SupabaseClient | null> {
  if (client) return client;
  if (clientFailed || !supabaseConfigured()) return null;
  try {
    const { createClient } = await import('@supabase/supabase-js');
    client = createClient(url(), key(), {
      auth: { persistSession: true, autoRefreshToken: true, storageKey: 'euclid.auth' },
      // 30Hz of cursors is the load this connection is sized for. The default of 10 events per
      // second silently drops frames above it, and a dropped cursor frame reads as lag.
      realtime: { params: { eventsPerSecond: 40 } },
    });
    return client;
  } catch {
    clientFailed = true;
    return null;
  }
}

// ── Why there is no server ─────────────────────────────────────────────────────────────────

export type OfflineReason =
  /** No `.env.local`, or a build made without one. */
  | 'no-credentials'
  /** Anonymous sign-in is switched off in the Supabase dashboard. */
  | 'anonymous-disabled'
  /** The network, DNS, or a paused project. */
  | 'unreachable'
  /** Anything else the auth endpoint said. */
  | 'refused';

export type SignIn =
  | { ok: true; user: User }
  | { ok: false; reason: OfflineReason };

/**
 * One plain sentence, for the status line. Never a code, never a stack.
 *
 * Each of these names the cause and says what still works, because the honest answer to "the
 * server is not there" in a local-first app is "nothing you were doing has stopped".
 */
export const explain = (reason: OfflineReason): string => ({
  'no-credentials':
    'No server is configured for this build, so teams stay on this device.',
  'anonymous-disabled':
    'Anonymous sign-in is switched off in the Supabase project, so teams stay on this device.',
  'unreachable':
    'The server cannot be reached, so teams stay on this device until it is back.',
  'refused':
    'The server refused the sign-in, so teams stay on this device.',
}[reason]);

/**
 * Sign in anonymously — the identity model of `supabase/0001_teams.sql`.
 *
 * No email, no password, no account to create: a device gets a stable uid, a `profiles` row
 * appears by trigger, and that uid is what every row-level-security policy keys on. A shared
 * publishable key alone gives the policies nothing to check, which is why this exists at all.
 *
 * The one failure worth naming separately is `anonymous_provider_disabled`, because it is a
 * setting rather than a fault: the schema is applied and correct, and the app is one dashboard
 * toggle away from working. Reporting it as a generic error would send someone looking at the
 * code instead.
 */
export async function signInAnonymously(): Promise<SignIn> {
  const supabase = await getSupabase();
  if (!supabase) return { ok: false, reason: 'no-credentials' };

  try {
    const existing = await supabase.auth.getSession();
    const user = existing.data.session?.user;
    if (user) return { ok: true, user };

    const { data, error } = await supabase.auth.signInAnonymously();
    if (error) {
      // `code` is the stable identifier; `message` is prose and has changed between releases.
      const code = (error as { code?: string }).code ?? '';
      if (code === 'anonymous_provider_disabled') return { ok: false, reason: 'anonymous-disabled' };
      if (error.status === undefined || error.status === 0) return { ok: false, reason: 'unreachable' };
      return { ok: false, reason: 'refused' };
    }
    if (!data.user) return { ok: false, reason: 'refused' };
    return { ok: true, user: data.user };
  } catch {
    // A thrown fetch is the network, not the service. Nothing here inspects the exception: it
    // can carry the request URL, and the request URL carries the key.
    return { ok: false, reason: 'unreachable' };
  }
}

/**
 * Hand the current access token to the Realtime socket.
 *
 * Postgres changes are delivered through the same row-level-security policies as a `select`, and
 * the socket authenticates separately from the REST calls. Without this a subscription connects,
 * reports `SUBSCRIBED`, and then silently delivers nothing — which looks exactly like a peer who
 * is not drawing.
 */
export async function authoriseRealtime(supabase: SupabaseClient): Promise<void> {
  try {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (token) await supabase.realtime.setAuth(token);
  } catch { /* the channel will report its own failure */ }
}
